import { lazy } from "react";

/**
 * Универсальная обертка для React.lazy с поддержкой именованных экспортов
 * 
 * @param {Function} importFn - Функция динамического импорта компонента (например: () => import('./Component'))
 * @param {string} [exportName='default'] - Имя экспорта для компонента (используется для именованных экспортов)
 * @returns {React.LazyExoticComponent} React-компонент для ленивой загрузки
 * 
 * @throws {Error} Если указанный экспорт не найден в модуле
 * 
 * @example
 * // Для default экспорта:
 * const LazyComponent = dynamicImport(() => import('./Component'));
 * 
 * @example
 * // Для именованного экспорта:
 * const LazyComponent = dynamicImport(() => import('./Component'), 'NamedExport');
 * 
 * @example
 * // Использование с Suspense:
 * function MyComponent() {
 *   return (
 *     <Suspense fallback={<div>Loading...</div>}>
 *       <LazyComponent />
 *     </Suspense>
 *   );
 * }
 * 
 * @note
 * Для корректной работы убедитесь, что:
 * 1. Компонент экспортируется корректно (export default или именованный экспорт)
 * 2. Путь к компоненту указан верно
 * 3. Компонент является валидным React-компонентом
 * 
 * @see {@link https://reactjs.org/docs/code-splitting.html#reactlazy} Документация React.lazy
 */
export const dynamicImport = (importFn, exportName = 'default') => {
  return lazy(() => 
    importFn()
      .then(module => ({
        default: exportName === 'default' ? module.default : module[exportName]
      }))
      .catch(error => {
        console.error('Dynamic import error:', error);
        throw new Error(`Failed to load component: ${error.message}`);
      })
  );
};